import React, { createContext, useContext, useState, useRef, useCallback, useEffect } from 'react';

export interface PodcastEpisode {
  id: string;
  title: string;
  podcastTitle: string;
  audioUrl: string;
  artworkUrl?: string;
  duration?: number; // seconds
}

interface PodcastPlayerState {
  episode: PodcastEpisode | null;
  isPlaying: boolean;
  currentTime: number;
  duration: number;
  playbackRate: number;
  volume: number;
  isVisible: boolean;
}

interface PodcastPlayerContextValue extends PodcastPlayerState {
  play: (episode: PodcastEpisode) => void;
  pause: () => void;
  resume: () => void;
  togglePlay: () => void;
  seek: (time: number) => void;
  skipForward: (seconds?: number) => void;
  skipBackward: (seconds?: number) => void;
  setPlaybackRate: (rate: number) => void;
  setVolume: (vol: number) => void;
  close: () => void;
  audioRef: React.RefObject<HTMLAudioElement | null>;
}

const PodcastPlayerContext = createContext<PodcastPlayerContextValue | null>(null);

export function usePodcastPlayer() {
  const ctx = useContext(PodcastPlayerContext);
  if (!ctx) throw new Error('usePodcastPlayer must be used within PodcastPlayerProvider');
  return ctx;
}

export function PodcastPlayerProvider({ children }: { children: React.ReactNode }) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [state, setState] = useState<PodcastPlayerState>({
    episode: null,
    isPlaying: false,
    currentTime: 0,
    duration: 0,
    playbackRate: 1,
    volume: 1,
    isVisible: false,
  });

  // Create audio element once
  useEffect(() => {
    const audio = new Audio();
    audio.preload = 'metadata';
    audioRef.current = audio;

    const onTimeUpdate = () => {
      setState(s => ({ ...s, currentTime: audio.currentTime }));
    };
    const onLoadedMetadata = () => {
      setState(s => ({ ...s, duration: audio.duration || 0 }));
    };
    const onEnded = () => {
      setState(s => ({ ...s, isPlaying: false }));
    };
    const onPlay = () => {
      setState(s => ({ ...s, isPlaying: true }));
    };
    const onPause = () => {
      setState(s => ({ ...s, isPlaying: false }));
    };

    audio.addEventListener('timeupdate', onTimeUpdate);
    audio.addEventListener('loadedmetadata', onLoadedMetadata);
    audio.addEventListener('ended', onEnded);
    audio.addEventListener('play', onPlay);
    audio.addEventListener('pause', onPause);

    return () => {
      audio.removeEventListener('timeupdate', onTimeUpdate);
      audio.removeEventListener('loadedmetadata', onLoadedMetadata);
      audio.removeEventListener('ended', onEnded);
      audio.removeEventListener('play', onPlay);
      audio.removeEventListener('pause', onPause);
      audio.pause();
      audio.src = '';
    };
  }, []);

  // Media Session API for lock screen controls
  useEffect(() => {
    if (!('mediaSession' in navigator) || !state.episode) return;

    navigator.mediaSession.metadata = new MediaMetadata({
      title: state.episode.title,
      artist: state.episode.podcastTitle,
      artwork: state.episode.artworkUrl
        ? [{ src: state.episode.artworkUrl, sizes: '512x512', type: 'image/jpeg' }]
        : [],
    });

    navigator.mediaSession.setActionHandler('play', () => {
      audioRef.current?.play();
    });
    navigator.mediaSession.setActionHandler('pause', () => {
      audioRef.current?.pause();
    });
    navigator.mediaSession.setActionHandler('seekbackward', () => {
      if (audioRef.current) audioRef.current.currentTime = Math.max(0, audioRef.current.currentTime - 15);
    });
    navigator.mediaSession.setActionHandler('seekforward', () => {
      if (audioRef.current) {
        audioRef.current.currentTime = Math.min(audioRef.current.duration || 0, audioRef.current.currentTime + 15);
      }
    });
    navigator.mediaSession.setActionHandler('seekto', (details) => {
      if (audioRef.current && details.seekTime != null) {
        audioRef.current.currentTime = details.seekTime;
      }
    });
  }, [state.episode]);

  const play = useCallback((episode: PodcastEpisode) => {
    const audio = audioRef.current;
    if (!audio) return;

    audio.src = episode.audioUrl;
    audio.load();
    audio.play().catch(() => {});
    setState(s => ({
      ...s,
      episode,
      isPlaying: true,
      currentTime: 0,
      duration: episode.duration || 0,
      isVisible: true,
    }));
  }, []);

  const pause = useCallback(() => {
    audioRef.current?.pause();
  }, []);

  const resume = useCallback(() => {
    audioRef.current?.play().catch(() => {});
  }, []);

  const togglePlay = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.paused) {
      audio.play().catch(() => {});
    } else {
      audio.pause();
    }
  }, []);

  const seek = useCallback((time: number) => {
    if (audioRef.current) {
      audioRef.current.currentTime = time;
    }
  }, []);

  const skipForward = useCallback((seconds = 15) => {
    if (audioRef.current) {
      audioRef.current.currentTime = Math.min(
        audioRef.current.duration || 0,
        audioRef.current.currentTime + seconds
      );
    }
  }, []);

  const skipBackward = useCallback((seconds = 15) => {
    if (audioRef.current) {
      audioRef.current.currentTime = Math.max(0, audioRef.current.currentTime - seconds);
    }
  }, []);

  const setPlaybackRate = useCallback((rate: number) => {
    if (audioRef.current) {
      audioRef.current.playbackRate = rate;
    }
    setState(s => ({ ...s, playbackRate: rate }));
  }, []);

  const setVolume = useCallback((vol: number) => {
    const clamped = Math.max(0, Math.min(1, vol));
    if (audioRef.current) {
      audioRef.current.volume = clamped;
    }
    setState(s => ({ ...s, volume: clamped }));
  }, []);

  const close = useCallback(() => {
    const audio = audioRef.current;
    if (audio) {
      audio.pause();
      audio.src = '';
    }
    setState(s => ({
      ...s,
      episode: null,
      isPlaying: false,
      currentTime: 0,
      duration: 0,
      isVisible: false,
    }));
  }, []);

  return (
    <PodcastPlayerContext.Provider
      value={{
        ...state,
        play,
        pause,
        resume,
        togglePlay,
        seek,
        skipForward,
        skipBackward,
        setPlaybackRate,
        setVolume,
        close,
        audioRef,
      }}
    >
      {children}
    </PodcastPlayerContext.Provider>
  );
}
